<script>
    import Vue from "vue";
    import utils from "@/utils.js";
    import {timeFormat} from "d3-time-format";
    import {timeParse} from "d3-time-format";

    export default {
        name: "PasswordEdit",
        props: {
            creds: Object,
        },
        data() {
            return {
                showingPass: (this.creds.pass || "").length == 0,
                showingNotes: (this.creds.notes || []).length == 0,
                // want a copy of creds, and not a props object
                credsEdit: JSON.parse(JSON.stringify(this.creds)),
                unsubscribeActions: null,
                showing: "edit",
                scrollX: 0,
                scrollY: 0,
            };
        },
        methods: {
            dismissEdit() {
                this.$emit("save");
            },
            deletePass() {
                let credsValues = JSON.parse(JSON.stringify(this.credsEdit));
                this.$emit("delete", credsValues);
            },
            saveChanges(e) {
                let credsValues = JSON.parse(JSON.stringify(this.credsEdit));
                this.$emit("save", credsValues);
            },
            togglePass() {
                this.showingPass = !this.showingPass;
            },
            randomPass() {
                this.credsEdit = {...this.credsEdit, pass: utils.randomPass()};
            },
            toggleNotes() {
                this.showingNotes = !this.showingNotes;
                Vue.nextTick(() => {
                    if (this.showingNotes) {
                        this.$refs.notes.focus();
                    }
                });
            },

            toggle(viewName) {
                this.showing = viewName;
            },

            formatTs(ts) {
                ts = timeParse("%Y-%m-%d %H:%M:%S")(ts);
                let now = new Date();
                if (now.getFullYear() == ts.getFullYear()) {
                    return timeFormat("%b %d, %H:%M")(ts);
                } else {
                    return timeFormat("%b %d %Y, %H:%M")(ts);
                }
            },

            rejectBodyScroll(evt) {
                // scroll on textarea ends up bleeding into the body under it - don't want
                window.scrollTo(this.scrollX, this.scrollY);
            },
        },

        mounted() {
            this.scrollX = window.scrollX;
            this.scrollY = window.scrollY;
            window.addEventListener("scroll", this.rejectBodyScroll);

            this.$refs.site.focus();
            this.unsubscribeActions = this.$store.subscribeAction((action, state) => {
                if (action.type == "requestLock") {
                    this.$store.commit("setBackup", {
                        editing: "password",
                        creds: this.credsEdit,
                    });
                    this.$store.commit("encryptPack");
                }
            });
        },

        beforeDestroy() {
            window.removeEventListener("scroll", this.rejectBodyScroll);
            this.unsubscribeActions();
        },
    };
</script>


<template>
    <div class="password-edit">
        <div class="modal-bg" @click="dismissEdit()" />
        <div class="modal-container">
            <div class="dialog" v-show="showing == 'edit'">
                <div class="contents">
                    <div class="site">
                        <input type="text" v-model="credsEdit.site" ref="site" placeholder="Site" autocomplete="off" />
                    </div>

                    <material-input label="Web Address" v-model.lazy="credsEdit.url" />

                    <material-input label="Email / ID" v-model.lazy="credsEdit.login" />

                    <div class="password-field">
                        <material-input
                            type="password"
                            label="Password"
                            readonly
                            v-model.lazy="credsEdit.pass"
                            v-show="!showingPass"
                        >
                            <div class="actions">
                                <button @click="togglePass()" v-if="credsEdit.id">
                                    <i class="material-icons">visibility</i>
                                </button>
                            </div>
                        </material-input>
                        <material-input type="text" label="Password" v-model.lazy="credsEdit.pass" v-show="showingPass">
                            <div class="actions">
                                <button @click="togglePass()" v-if="credsEdit.id && creds.pass">
                                    <i class="material-icons">visibility_off</i>
                                </button>
                                <button @click="randomPass()">
                                    <i class="material-icons">cached</i>
                                </button>
                            </div>
                        </material-input>
                    </div>

                    <div class="notes-block">
                        <div class="notes-header">
                            <label>
                                Notes
                            </label>
                            <button @click.prevent="toggleNotes()" v-if="creds.notes">
                                <i class="material-icons" v-if="!showingNotes">visibility</i>
                                <i class="material-icons" v-if="showingNotes">visibility_off</i>
                            </button>
                        </div>

                        <div class="notes-contents">
                            <textarea readonly v-show="!showingNotes">•••</textarea>
                            <textarea name="notes" v-show="showingNotes" v-model="credsEdit.notes" ref="notes" />
                        </div>
                    </div>
                </div>

                <div class="controls">
                    <button
                        @click="toggle('history')"
                        class="history-button"
                        v-show="(credsEdit.history || []).length > 1"
                    >
                        <Emoji name="timer" />
                    </button>
                    <button @click="deletePass()">
                        Delete
                    </button>
                    <div class="spacer"></div>
                    <button @click="dismissEdit()">
                        Cancel
                    </button>
                    <button @click="saveChanges()">
                        Save
                    </button>
                </div>
            </div>

            <div class="dialog history" v-if="showing == 'history'">
                <div class="contents">
                    <h1>
                        Edit History
                    </h1>

                    <div class="dates">
                        <div class="date-row" v-for="[ts, historyRec] in creds.history" :key="ts">
                            <div class="heading">
                                {{ formatTs(ts) }}
                            </div>

                            <div class="fields">
                                <template v-for="([field, val], index) in historyRec">
                                    <div :key="`field-${index}`" class="field">{{ field }}:</div>
                                    <div :key="`val-${index}`">
                                        {{ val }}
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="controls">
                    <button @click="toggle('edit')" class="history-button">
                        <Emoji name="pen" />
                    </button>
                    <div class="spacer" />
                    <button @click="dismissEdit()">
                        Close
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>


<style lang="scss">
    .password-edit {
        button {
            padding: 12px 25px;
            text-decoration: none;
            color: #666;

            &:hover {
                background: #fff;
            }

            &:hover,
            &:focus {
                text-decoration: none;
                outline: none;
            }
        }

        .history {
            h1 {
                margin-top: 0;
                color: #888;
                font-size: 2em;
            }

            .heading {
                font-weight: 600;
                margin-bottom: 0.5em;
            }

            .dates {
                display: grid;
                gap: 1.5em;
                max-height: 24em;
                overflow-y: auto;
            }

            .fields {
                margin-left: 1em;
                display: grid;
                grid-template-columns: 1fr 10fr;

                .field {
                    text-align: right;
                    margin-right: 1em;
                    width: 3em;
                    text-transform: capitalize;
                }
            }
        }

        .history-button {
            flex-grow: 0;
            padding: 0;

            .emoji {
                max-width: 30px;
                max-height: 30px;
            }
        }

        .header {
            font-weight: 500;
            font-size: 1.5em;
            margin-bottom: 1em;
        }

        .contents {
            font-size: 1em;

            & > div {
                margin-bottom: 2em;

                &:last-child {
                    margin-bottom: 0;
                }
            }

            label {
                display: block;
                margin-bottom: 4px;
                color: #999;
            }

            .hide-pass {
                font-size: 1em;
                color: #555;
                padding: 0;
                padding-bottom: 5px;
                margin-top: 5px;
                width: 100%;

                background: none;
                border: none;
                border-bottom: 1px solid #ccc;

                &:focus {
                    box-shadow: none;
                    outline: none;
                    border-color: #000;
                    color: #000;
                }
            }

            .site input {
                border: none;
                font-size: 2em;
                color: #333;

                &:focus {
                    outline: none;
                }
            }

            .password-field {
                display: flex;
                height: 2em;
                button {
                    margin-left: 0.5em;
                    color: #333;
                }

                .material-input {
                    flex-grow: 1;
                }

                .actions {
                    margin-top: 2px;

                    button {
                        display: flex;
                        padding: 4px 10px;
                        color: #444;

                        &:hover,
                        &:focus {
                            background: none;
                        }
                    }

                    .material-icons {
                        font-size: 18px;
                    }
                }
            }

            .notes-block {
                margin-top: 3em;
                background: #f6f6f6;
                align-items: flex-start;
                position: relative;

                .notes-header {
                    position: absolute;
                    left: 0;
                    right: 0;
                    margin-right: 20px;
                    display: flex;
                    flex-grow: 1;
                    align-items: center;
                    background: #f6f6f6;

                    label {
                        padding: 8px 12px;

                        flex-grow: 1;
                        color: $input-accent-color;
                        font-size: 13px;
                    }

                    button {
                        display: flex;
                        padding: 8px;
                        i {
                            font-size: 18px;
                        }
                    }
                }

                textarea {
                    resize: none;
                    border: none;
                    background: none;
                    font-family: sans serif;
                    font-size: 1em;
                    height: 120px;
                    width: 100%;
                    padding: 12px;
                    padding-top: 35px;
                    overflow-y: scroll;

                    &:focus {
                        outline: none;
                    }
                }

                button {
                    padding: 15px;
                    color: #333;

                    &:focus {
                        outline: none;
                    }

                    &:hover {
                        background: transparent;
                    }
                }
            }
        }

        .controls {
            display: flex;
            .spacer {
                flex-grow: 1;
            }

            & > * {
                margin-right: 0.5em;

                &:last-child {
                    margin-right: 0;
                }
            }
        }

        @media (max-width: $mobile-width-break) {
            .contents > div {
                margin-bottom: 10px;

                &.site {
                    margin-bottom: 1em;
                }
            }

            .contents .notes-block textarea {
                height: 90px;
            }
        }
    }
</style>


